import { useState } from "react"
import { ArrowRightOutlined } from '@ant-design/icons';
import {
	Checkbox,
	Col,
	Collapse,
	List,
	Row,
	Tag,
	Tooltip,
	Typography,
} from 'antd';
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd';
import { CoreField, CoreType, CoreDataPoint } from './CoreComponents';
import { AHRBSDataPoints } from './AHRBSDataPoints';
import { AHRBDataPointCustomizationMap } from '../../Interfaces/Reports/AHRBDataPointConfigurationModels';
import { AHRBDataPointAggregateMap } from '../../Interfaces/Reports/AHRBDataPointConfigurationModels';
import { AHRBDataPointFixedWhereMap } from '../../Interfaces/Reports/AHRBDataPointConfigurationModels';
import AdHocReportVariableWhereModel from '../../Interfaces/Requests/Reports/AdHocReportVariableWhereModel';

type AHRBuilderSelectionsProps = {
    coreTypes: CoreType[];
    coreFields: CoreField[];
    brandID: number;

	initialSelectedCoreDataPoints: CoreDataPoint[];
	initialSelectedOrderByDataPointID: number | undefined;
	initialDataPointCustomizations: AHRBDataPointCustomizationMap;
	initialDataPointAggregates: AHRBDataPointAggregateMap;
	initialDataPointFixedWheres: AHRBDataPointFixedWhereMap;
	initialDataPointVariableWheres: AdHocReportVariableWhereModel[];

    handleSetSelectedCoreDataPoints: (
        coreDataPoints: CoreDataPoint[]
    ) => void;
    handleSetSelectedOrderByDataPointID: (
        selectedOrderByDataPointID: number | undefined
    ) => void;
    handleSetDataPointCustomizations: (
        dataPointCustomizations: AHRBDataPointCustomizationMap
    ) => void;
    handleSetDataPointAggregates: (
        dataPointAggregates: AHRBDataPointAggregateMap
    ) => void;
    handleSetDataPointFixedWheres: (
    	dataPointFixedWheres: AHRBDataPointFixedWhereMap
	) => void;
	handleSetDataPointVariableWheres: (
		dataPointVariableWheres: AdHocReportVariableWhereModel[]
	) => void;
};

export const AHRBuilderSelections: React.FC<AHRBuilderSelectionsProps> = ({ ...props }) => {

	const { Text } = Typography;

	const initialSelectedDataPoints = props.initialSelectedCoreDataPoints ?? [];
	const initialSelectedCoreTypeIDs = Array.from(new Set(initialSelectedDataPoints.map(dataPoint => dataPoint.idType)));
	const initialSelectedDataPointIDs = new Set(initialSelectedDataPoints.map(dataPoint => dataPoint.id));
	const [selectedCoreTypeIDs, setSelectedCoreTypeIDs] = useState<number[]>(initialSelectedCoreTypeIDs);
	const [selectedCoreTypes, setSelectedCoreTypes] = useState<CoreType[]>(props.coreTypes.filter(coreType => initialSelectedCoreTypeIDs.includes(coreType.id)));
	const [selectedCoreFields, setSelectedCoreFields] = useState<CoreField[]>(
		props.coreFields.filter(field => field.idBrand === props.brandID && initialSelectedCoreTypeIDs.includes(field.idType) && !initialSelectedDataPointIDs.has(field.id))
	);
	const [selectedCoreDataPoints, setSelectedCoreDataPoints] = useState<CoreDataPoint[]>(initialSelectedDataPoints);
	const [selectedOrderByDataPointID, setSelectedOrderByDataPointID] = useState<number | undefined>(props.initialSelectedOrderByDataPointID);
	const [dataPointCustomizations, setDataPointCustomizations] = useState<AHRBDataPointCustomizationMap>(props.initialDataPointCustomizations ?? {});
	const [dataPointAggregates, setDataPointAggregates] = useState<AHRBDataPointAggregateMap>(props.initialDataPointAggregates ?? {});
	const [dataPointFixedWheres, setDataPointFixedWheres] = useState<AHRBDataPointFixedWhereMap>(props.initialDataPointFixedWheres ?? {});
	const [dataPointVariableWheres, setDataPointVariableWheres] = useState<AdHocReportVariableWhereModel[]>(props.initialDataPointVariableWheres ?? []);

	const applySelectedDataPointSort = (dataPoints: CoreDataPoint[]): CoreDataPoint[] => {
		return dataPoints.map((dataPoint, index) => ({
			...dataPoint,
			sort: index
		}));
	};

	const getTypeName = (coreType: CoreType) => coreType.name;

	const getCoreDataPointDataTypeDisplayText = (
		dataType: string | number | undefined
	): string => {
		if (dataType === undefined || dataType === null) {
			return '';
		}

		const normalizedDataType = dataType.toString().toUpperCase();

		switch (normalizedDataType) {
			case '1':
			case 'INTEGER':
				return 'Integer';

			case '2':
			case 'FLOAT':
				return 'Float';

			case '3':
			case 'STRING':
				return 'String';

			case '4':
			case 'DATETIME':
				return 'DateTime';

			case '5':
			case 'BINARY':
				return 'Binary';

			case '6':
			case 'LOOKUP':
				return 'Lookup';

			case '7':
			case 'NOTUSED':
				return 'Not Used';

			case '8':
			case 'AUTONUMBER':
				return 'Autonumber';

			case '9':
			case 'PHONENUMBER':
				return 'Phone Number';

			case '10':
			case 'DATE':
				return 'Date';

			case '11':
			case 'TENANTTRACKABLE':
				return 'Tenant Trackable';

			default:
				return dataType.toString();
		}
	};

	const handleSetDataPointCustomizations = (newDataPointCustomizations: AHRBDataPointCustomizationMap) => {
		setDataPointCustomizations(newDataPointCustomizations);
	    props.handleSetDataPointCustomizations(newDataPointCustomizations);
	};

	const handleSetDataPointAggregates = (newDataPointAggregates: AHRBDataPointAggregateMap) => {
		setDataPointAggregates(newDataPointAggregates);
	    props.handleSetDataPointAggregates(newDataPointAggregates);
	};

	const handleSetDataPointFixedWheres = (newDataPointFixedWheres: AHRBDataPointFixedWhereMap) => {
		setDataPointFixedWheres(newDataPointFixedWheres);
		props.handleSetDataPointFixedWheres(newDataPointFixedWheres);
	};

	const handleSetDataPointVariableWheres = (newDataPointVariableWheres: AdHocReportVariableWhereModel[]) => {
		setDataPointVariableWheres(newDataPointVariableWheres);
		props.handleSetDataPointVariableWheres(newDataPointVariableWheres);
	};

	const handleSetSelectedOrderByDataPointID = (newSelectedOrderByDataPointID: number | undefined) => {
		setSelectedOrderByDataPointID(newSelectedOrderByDataPointID);
	    props.handleSetSelectedOrderByDataPointID(newSelectedOrderByDataPointID);
	};

	const removeDataPointRelatedConfiguration = (removedDataPointIDs: number[]) => {
		setDataPointCustomizations(currentDataPointCustomizations => {
			const newDataPointCustomizations = { ...currentDataPointCustomizations };

			removedDataPointIDs.forEach(function (removedDataPointID) {
				delete newDataPointCustomizations[removedDataPointID];
			});

			props.handleSetDataPointCustomizations(newDataPointCustomizations);

			return newDataPointCustomizations;
		});

		setDataPointAggregates(currentDataPointAggregates => {
			const newDataPointAggregates = { ...currentDataPointAggregates };

			removedDataPointIDs.forEach(function (removedDataPointID) {
				delete newDataPointAggregates[removedDataPointID];
			});

			props.handleSetDataPointAggregates(newDataPointAggregates);

			return newDataPointAggregates;
		});

		setDataPointFixedWheres(currentDataPointFixedWheres => {
			const newDataPointFixedWheres = { ...currentDataPointFixedWheres };

			removedDataPointIDs.forEach(function (removedDataPointID) {
				delete newDataPointFixedWheres[removedDataPointID];
			});

			props.handleSetDataPointFixedWheres(newDataPointFixedWheres);

			return newDataPointFixedWheres;
		});

		setDataPointVariableWheres(currentDataPointVariableWheres => {
			const newDataPointVariableWheres = [...currentDataPointVariableWheres];

			removedDataPointIDs.forEach(function (removedDataPointID) {
				delete newDataPointVariableWheres[removedDataPointID];
			});

			props.handleSetDataPointVariableWheres(newDataPointVariableWheres);

			return newDataPointVariableWheres;
		});

		setSelectedOrderByDataPointID(currentSelectedOrderByDataPointID => {
			const newSelectedOrderByDataPointID =
				currentSelectedOrderByDataPointID !== undefined &&
				removedDataPointIDs.includes(currentSelectedOrderByDataPointID)
					? undefined
					: currentSelectedOrderByDataPointID;

			if (newSelectedOrderByDataPointID !== currentSelectedOrderByDataPointID) {
				props.handleSetSelectedOrderByDataPointID(newSelectedOrderByDataPointID);
			}

			return newSelectedOrderByDataPointID;
		});
	};

	const onDragEnd = (dropResult: DropResult) => {
		// ---- DEBUG
		// console.log(selectedCoreFields);
		// console.log('dropResult', dropResult);
		// ---- END DEBUG

		const { destination, source, draggableId } = dropResult;
		const fieldId = +draggableId;

		// ---- DEBUG
		// console.log('SI ' + source.index);
		// console.log('DI ' + (destination?.index ?? 'null'));
		// ---- END DEBUG

		if (!destination) {
			return;
		}

		if (
			source.droppableId === 'dataPoints' &&
			destination.droppableId === 'dataPoints'
		) {
			if (source.index === destination.index) {
				return;
			}

			const reorderedDataPoints = [...selectedCoreDataPoints];
			const [movedDataPoint] = reorderedDataPoints.splice(source.index, 1);

			if (!movedDataPoint) {
				return;
			}

			reorderedDataPoints.splice(destination.index, 0, movedDataPoint);

			const newDataPoints = applySelectedDataPointSort(reorderedDataPoints);

			setSelectedCoreDataPoints(newDataPoints);
			props.handleSetSelectedCoreDataPoints(newDataPoints);

			return;
		}

		if (destination.droppableId === source.droppableId) {
			return;
		}

		const isAdding = destination.droppableId === 'dataPoints';

		if (isAdding) {
			// ADDING CASE

			const selectedCoreField = selectedCoreFields.find(x => x.id === fieldId);

    		if (!selectedCoreField) return;

			const newItem = {
				id: selectedCoreField.id,
				idBrand: selectedCoreField.idBrand,
				name: selectedCoreField.name,
				dataType: selectedCoreField.dataType,
				idType: selectedCoreField.idType,
				idField: selectedCoreField.id,
				idLookupType: selectedCoreField.idLookupType,
				isPHI: selectedCoreField.isPHI,
				guid: selectedCoreField.guid,
				dbName: selectedCoreField.dbName,

				sort: 0,
				filter: '',

				isAdded: true,
				isUpdated: false
			};

			const newDataPoints = applySelectedDataPointSort([...selectedCoreDataPoints, newItem]);

			// ---- DEBUG
			// console.log('Adding →', newItem);
			// console.log('Next dataPoints →', newDataPoints);
			// ---- END DEBUG

			setSelectedCoreDataPoints(newDataPoints);
			props.handleSetSelectedCoreDataPoints(newDataPoints); // ✅ not stale

			setSelectedCoreFields(prev => prev.filter(field => field.id !== fieldId));
		} else {
			// REMOVAL CASE

			const newDataPoints = applySelectedDataPointSort(
				selectedCoreDataPoints.filter(x => x.id !== fieldId)
			);

			removeDataPointRelatedConfiguration([fieldId]);

			// ---- DEBUG
			// console.log('Removing fieldId →', fieldId);
			// console.log('Next dataPoints →', newDataPoints);
			// ---- END DEBUG

			setSelectedCoreDataPoints(newDataPoints);

			props.handleSetSelectedCoreDataPoints(newDataPoints); // ✅ not stale

			setSelectedCoreFields
				(
					prev => [...prev, ...props.coreFields
						.filter(x => x.id === fieldId)]
						.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
				);
		}
	};

	/*const onCoreTypeChecked = (typeId: number, isChecked: boolean) => {
		if (isChecked) {
			setSelectedCoreTypeIDs((prev) =>
				[...prev, typeId]
			);
			console.log('Core TypeID')
			console.log(typeId)

			setSelectedCoreTypes((prev) =>
				[...prev, ...props.coreTypes.filter(x => x.id === typeId)]
			);
			console.log('Core Types')
			console.log(props.coreTypes.filter(x => x.id === typeId).sort((a, b) => ((a.id) - (b.id))))

			setSelectedCoreFields((prev) =>
				[...prev, ...props.coreFields.filter(x => x.idType === typeId)]
			);
			console.log('Core Fields')
			console.log(props.coreFields.filter(x => x.idType === typeId).sort((a, b) => ((a.id) - (b.id))))
		} else {
			setSelectedCoreTypeIDs((prev) =>
				prev.filter((type) => type !== typeId)
			);

			setSelectedCoreTypes((prev) =>
				prev.filter((type) => type.id !== typeId)
			);

			setSelectedCoreFields(prev =>
				prev.filter((field) => field.idType !== typeId)
			);

			setSelectedCoreDataPoints(prev =>
				prev.filter((dataPoint) => dataPoint.idType !== typeId)
			);
		}
	};*/

	const onCoreTypeChecked = (typeId: number, isChecked: boolean) => {
		// ---- DEBUG 
		//	console.log('Added Type:', typeId);
		//	console.log('Fields Added:', relatedFields);
		// ---- END DEBUG

		const coreType = props.coreTypes.find(x => x.id === typeId);
		const selectedDataPointIDs = new Set(selectedCoreDataPoints.map(dataPoint => dataPoint.id));
		const relatedFields = props.coreFields.filter(field => field.idType === typeId && !selectedDataPointIDs.has(field.id));

		if (isChecked) {
			if (!coreType) return;

			// ADD

			setSelectedCoreTypeIDs(prev => prev.includes(typeId) ? prev : [...prev, typeId]);

			setSelectedCoreTypes(prev => prev.some(t => t.id === typeId) ? prev : [...prev, coreType]);

			setSelectedCoreFields(
				prev => {
					const newFields = relatedFields.filter(f => !prev.some(existing => existing.id === f.id));
					return [...prev, ...newFields];
				}
			);
		} else {
			// REMOVE

			setSelectedCoreTypeIDs(prev =>
				prev.filter(type => type !== typeId)
			);

			setSelectedCoreTypes(prev =>
				prev.filter(type => type.id !== typeId)
			);

			setSelectedCoreFields(prev =>
				prev.filter(field => field.idType !== typeId)
			);

			const removedDataPointIDs = selectedCoreDataPoints.filter(dataPoint => dataPoint.idType === typeId).map(dataPoint => dataPoint.id);

			// Remove customization, aggregate, group-by, and order-by state for removed data points
			removeDataPointRelatedConfiguration(removedDataPointIDs);

			setSelectedCoreDataPoints(prev => {
				const newDataPoints = applySelectedDataPointSort(
					prev.filter(dataPoint => dataPoint.idType !== typeId)
				);

				props.handleSetSelectedCoreDataPoints(newDataPoints);

				return newDataPoints;
			});

			// ---- DEBUG 
			//  console.log('Removed Type:', typeId);
			// ---- END DEBUG 
		}
	};

	const flattenedFields = selectedCoreTypeIDs.flatMap((coreTypeID) => {
		const coreType = props.coreTypes.find(x => x.id === coreTypeID);
		const fields = selectedCoreFields.filter(x => x.idType === coreTypeID);

		return Array.isArray(fields) ? fields.map(field => ({coreTypeID, coreTypeName: coreType?.name ?? '', field})) : [];
	});

	const selectedCoreTypeFieldCollapseItems = selectedCoreTypeIDs.map((coreTypeID) => {
		const coreType = props.coreTypes.find(x => x.id === coreTypeID);

		const fieldsForType = flattenedFields
			.map((item, globalIndex) => ({ ...item, globalIndex }))
			.filter(item => item.coreTypeID === coreTypeID);

		if (fieldsForType.length === 0) {
			return {
				children: (
					<p className='font-bold p-3'>
						{coreType?.name ?? ''}{' '}
						{(coreType?.name ?? '') === '' ? '' : ' - No fields available'}
					</p>
				),
				key: coreTypeID.toString(),
				label: coreType?.name ?? '',
				styles: {
					body: {
						padding: '4px 0px'
					}
				},
			};
		}

		return {
			key: coreTypeID.toString(),
			label: coreType?.name ?? '',
			children: (
				<>
					{fieldsForType.map(({ field, globalIndex }) => (
						<Draggable
							key={field.id}
							draggableId={field.id.toString()}
							index={globalIndex}
						>
							{(provided, snapshot) => (
								<div
									{...provided.draggableProps}
									{...provided.dragHandleProps}
									ref={provided.innerRef}
									style={{
										color: 'black',
										background: snapshot.isDragging ? 'lightgray' : 'white',
										border: snapshot.isDragging ? '2px solid blue' : '2px solid lightgray',
										borderRadius: '5px',
										marginBottom: '5px',
										padding: '5px',
										userSelect: "none",
										width: "100%",
										...provided.draggableProps.style
									}}
								>
									<Row style={{ textAlign: 'left' }}>
										<Col span={18} style={{ textAlign: 'left' }}>
											<span>{field.name}</span>
										</Col>
										<Col span={6} style={{ textAlign: 'right' }}>
											<span
												style={{
													display: 'inline-flex',
													alignItems: 'center',
													gap: '4px',
													whiteSpace: 'nowrap'
												}}
											>
												{
													field.isPHI
														? (
															<Tooltip title='This field contains Protected Health Information'>
																<Tag color='red' style={{ marginRight: 0 }}>
																	PHI
																</Tag>
															</Tooltip>
														)
														: null
												}
												<span style={{ fontSize: '10px' }}>
													{getCoreDataPointDataTypeDisplayText(field.dataType)}
												</span>
											</span>
										</Col>
									</Row>
								</div>
							)}
						</Draggable>
					))}
				</>
			),
		};
	});

	return (
		<>
			<Row className='p-2' gutter={8} style={{height: '95%'}}>
				<Col span={1} />
				<Col span={6}>
					<h3 className='text-2xl pt-2 px-2'>Object Types</h3>
					<Text type='secondary'>Select an Object Type to view its Fields</Text>
					<div style={{border: '1px solid black', height: '430px', overflow: 'auto', textAlign: 'left', verticalAlign: 'top'}}>
						<List
							className='p-2'
							itemLayout='horizontal'
							style={{}}
							split
							dataSource={props.coreTypes?.map(coreType => ({ ...coreType, enabled: !coreType.isDeleted && !coreType.isDisabled, name: getTypeName(coreType) }))}
							renderItem={({ name, id, enabled }) => (
								<List.Item key={id}>
									<Checkbox
										onChange={(e) => onCoreTypeChecked(id, e.target.checked)}
										disabled={!enabled}
										checked={!!selectedCoreTypeIDs.includes(id)}
									>
										<Tooltip title={name} mouseEnterDelay={0}>
											{name}
										</Tooltip>
									</Checkbox>
								</List.Item>
							)}
						/>
					</div>
				</Col>
				<Col span={1} className='mt-5 text-center'>
					<ArrowRightOutlined />
				</Col>
				<DragDropContext onDragEnd={onDragEnd}>
					<Col span={7}>
						<div
							style={{ display: 'flex', flexFlow: 'column', height: '100%' }} 
							className='border-solid border-gray-300 border-2 rounded-md h-full'
						>
							<h3 className='text-2xl p-2 mb-0'>Available Fields</h3>
							<Text type='secondary'>Click and drag Field <i>right</i> to select:</Text>
							<Droppable droppableId='fields'>
								{(provided, snapshot) => (
									<Col
										span={24}
										ref={provided.innerRef}
										{...provided.droppableProps}
										style={{
											background: snapshot.isDraggingOver ? (props.coreTypes ? 'lightgreen' : 'lightblue') : 'white',
											padding: '1px',
											border: '1px solid gray', 
											borderRadius: '5px',
											flex: '1 1 auto',
										}}
									>
										<Collapse
											ghost
											accordion
											style={{ height: '400px', overflowY: 'auto', padding: '0px' }}
											items={selectedCoreTypeFieldCollapseItems}
										/>
										{provided.placeholder}
									</Col>
								)}
							</Droppable>
						</div>
					</Col>
					<Col span={1} className='mt-5 text-center'>
						<ArrowRightOutlined />
					</Col>
					<Col span={7}>
						<AHRBSDataPoints
							coreTypes={props.coreTypes}
							selectedCoreTypeIDs={selectedCoreTypeIDs}
							selectedCoreTypes={selectedCoreTypes}
							selectedCoreDataPoints={selectedCoreDataPoints}
							brandID={props.brandID}
							selectedOrderByDataPointID={selectedOrderByDataPointID}
							dataPointCustomizations={dataPointCustomizations}
							dataPointAggregates={dataPointAggregates}
							dataPointFixedWheres={dataPointFixedWheres}
							dataPointVariableWheres={dataPointVariableWheres}
							handleSetSelectedOrderByDataPointID={handleSetSelectedOrderByDataPointID}
							handleSetDataPointCustomizations={handleSetDataPointCustomizations}
							handleSetDataPointAggregates={handleSetDataPointAggregates}
							handleSetDataPointFixedWheres={handleSetDataPointFixedWheres}
							handleSetDataPointVariableWheres={handleSetDataPointVariableWheres}
						/>
					</Col>
					<Col span={1} />
				</DragDropContext>
			</Row>
		</>
	);
};

export default AHRBuilderSelections;
